import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import {
  StoryboardDelta,
  UpdateClassesChange,
} from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { applyTailwindChangeToClasses } from "./applyChanges";
import { PropertyType } from "@/tailwind/tailwindUtils";
import { areClassesUnchanged } from ".";

/**
 * Gets a class update change for a single storyboard delta
 *
 * Note - if there is more than 1 storyboard delta, it only changes the first one
 */
export const getUpdateClassChangeForDeltas = (
  canvasId: string,
  storyboardDeltas: StoryboardDelta[],
): UpdateClassesChange | undefined => {
  if (!storyboardDeltas?.length) {
    return;
  }

  const singleUpdate =
    storyboardDeltas?.length === 1 &&
    storyboardDeltas[0].changeType === "update";

  if (!singleUpdate) {
    return;
  }

  const sbType =
    useCanvasStore.getState().storyboardIdToType[
      storyboardDeltas[0].storyboardId
    ];
  if (
    sbType === STORYBOARD_TYPES.APPLICATION ||
    sbType === STORYBOARD_TYPES.STORY
  ) {
    return;
  }

  const subCanvas = useSupabaseStore.getState().subCanvases[canvasId];
  const mainCanvas = useIssuesStore.getState().currentCanvas[canvasId];
  const canvas = subCanvas ?? mainCanvas;

  if (!canvas) {
    return;
  }

  const oldWidth = canvas?.storyboards.find(
    (sb) => sb.id === storyboardDeltas[0].storyboardId,
  )?.width;
  const oldHeight = canvas?.storyboards.find(
    (sb) => sb.id === storyboardDeltas[0].storyboardId,
  )?.height;

  const newWidth = storyboardDeltas[0].storyboardUpdateDict.width ?? oldWidth;
  const newHeight =
    storyboardDeltas[0].storyboardUpdateDict.height ?? oldHeight;

  if (newWidth === oldWidth && newHeight === oldHeight) {
    return;
  }

  const navTree =
    useCanvasStore.getState().storyboardToNavTree[
      storyboardDeltas[0].storyboardId
    ];
  if (!navTree?.tempoElement?.codebaseId) {
    return;
  }

  const treeElementLookup = useCodebaseStore.getState().treeElementLookup;
  const treeElement = treeElementLookup[navTree?.tempoElement.codebaseId];
  if (!treeElement) {
    return;
  }

  let currentElementClasses = treeElement.removableClasses ?? [];
  const allInvolvedClasses: any[] = [];
  if (oldWidth !== newWidth) {
    const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
      currentElementClasses,
      option: {
        tailwind: `w-[${newWidth}px]`,
        css: `width: ${newWidth}px`,
      },
      propertyType: PropertyType.WIDTH,
    });

    currentElementClasses = newClasses;
    allInvolvedClasses.push(...involvedClasses);
  }

  if (oldHeight !== newHeight) {
    const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
      currentElementClasses,
      option: {
        tailwind: `h-[${newHeight}px]`,
        css: `height: ${newHeight}px`,
      },
      propertyType: PropertyType.HEIGHT,
    });

    currentElementClasses = newClasses;
    allInvolvedClasses.push(...involvedClasses);
  }

  if (
    areClassesUnchanged(
      currentElementClasses,
      treeElement.removableClasses ?? [],
    )
  ) {
    return;
  }

  return new UpdateClassesChange({
    codebaseIdToUpdateClass: navTree?.tempoElement.codebaseId,
    newClasses: currentElementClasses,
    oldClasses: treeElement.removableClasses ?? [],
    involvedClasses: allInvolvedClasses,
    isInstantChangeOnly: false,
    isForStoryboardSize: true,
  });
};
